import { useState, useEffect, useRef } from 'react';
import { useBookingStore } from './store/bookingStore';
import logo from './assets/logo.webp';
import videoBg from './assets/video-bg.mp4';
import isaacPhoto from './assets/isaac-instructor.webp';
import damienPhoto from './assets/damien-yates.webp';
import gallery1 from './assets/gallery-1.webp';
import gallery2 from './assets/gallery-2.webp';
import gallery3 from './assets/gallery-3.webp';
import gallery4 from './assets/gallery-4.webp';
import gallery5 from './assets/gallery-5.webp';
import gallery6 from './assets/gallery-6.webp';
import gallery7 from './assets/gallery-7.webp';
import gallery8 from './assets/gallery-8.webp';
import gallery9 from './assets/gallery-9.webp';
import gallery10 from './assets/gallery-10.webp';
import gallery11 from './assets/gallery-11.webp';
import gallery12 from './assets/gallery-12.webp';
import aboutBg from './assets/about-bg.webp';
import liveFirePopup from './assets/live-fire-popup.jpeg';
import uslawshieldLogo from './assets/uslawshield-logo.png';

// ═══════════════════════════════════════════════════════════
// DATA
// ═══════════════════════════════════════════════════════════

const COURSES = [
  { id: 'pistol-fundamentals', name: 'Private Range Training', duration: '4 hours', price: 0, level: 'Contact Us', icon: '🎯', contactUs: true },
  { id: 'advanced-pistol', name: 'Advanced Pistol Tactics', duration: '6 hours', price: 250, level: 'Advanced', icon: '⚡' },
  { id: 'rifle-basics', name: 'Rifle Tactics', duration: '3 hours', price: 250, level: 'Beginner', icon: '🔫' },
  { id: 'long-distance', name: 'Long Distance Shooting', duration: '2 hours', price: 300, level: 'Advanced', icon: '🔭' },
  { id: 'shotgun-training', name: 'Night Training', duration: '3 hours', price: null, level: 'Intermediate', icon: '🌙' },
  { id: 'safety-fundamentals', name: 'Child Safety & Fundamentals', duration: '2 hours', price: 150, level: 'Beginner', icon: '🛡️' },
  { id: 'gun-cleaning', name: 'Gun Cleaning Course', duration: '2 hours', price: 75, level: 'Beginner', icon: '🔧' },
  { id: 'tourniquet-course', name: 'First Aid', duration: '3 hours', price: 150, level: 'Beginner', icon: '🩹' },
  { id: 'live-fire-tactics', name: 'Live Fire Tactics', duration: '3 hours', price: 250, level: 'Advanced', icon: '🔥' },
  { id: 'uts-program', name: 'UTS Program (Weekly)', duration: '3 hours', price: 75, level: 'All Levels', icon: '⚔️', recurring: 'Every Tuesday 7-10 PM' },
];

const INSTRUCTORS = [
  {
    name: 'Damien',
    title: 'Lead Instructor & Founder',
    military: 'U.S. Army — Combat Infantry',
    experience: '20+ years',
    specialties: ['Urban Tactics', 'Gun Fighting', 'Defensive Strategy', 'Advanced Firearms'],
    bio: 'Damien and Isaac bring more than 30 years of combined experience across private security, military service, firearms and tactical instruction, and professional training. Their backgrounds as both experienced instructors and dedicated lifelong students of their craft give them a practical understanding of what it takes to perform safely, confidently, and effectively under pressure. Through 3B Tactical, Damien and Isaac are committed to providing high-quality, realistic training built around safety, sound decision-making, tactical awareness, and skill development. Their mission is to help civilians, professional gun carriers, security professionals, and others develop the knowledge, confidence, and practical skills needed to respond effectively in real-world situations.',
    image: damienPhoto,
  },
  {
    name: 'Isaac',
    title: 'Tactical Instructor',
    military: 'Private Security & Military Service',
    experience: '10+ years',
    specialties: ['Private Security', 'Tactical Instruction', 'Professional Training', 'Defensive Strategy'],
    bio: 'Damien and Isaac bring more than 30 years of combined experience across private security, military service, firearms and tactical instruction, and professional training. Their backgrounds as both experienced instructors and dedicated lifelong students of their craft give them a practical understanding of what it takes to perform safely, confidently, and effectively under pressure. Through 3B Tactical, Damien and Isaac are committed to providing high-quality, realistic training built around safety, sound decision-making, tactical awareness, and skill development. Their mission is to help civilians, professional gun carriers, security professionals, and others develop the knowledge, confidence, and practical skills needed to respond effectively in real-world situations.',
    image: isaacPhoto,
  },
];

const GEAR_CHECKLIST: Record<string, string[]> = {
  'pistol-fundamentals': [
    'Handgun (semi-auto or revolver)',
    '2+ Magazines or speed loaders',
    '50 rounds of ammunition minimum',
    'Eye protection (ANSI Z87.1 rated)',
    'Ear protection (muffs or plugs)',
    'Sturdy belt & hip holster',
    'Closed-toe shoes (no sandals)',
    'Hat with brim',
    'Notepad & pen',
    'Water bottle',
  ],
  'advanced-pistol': [
    'Handgun (semi-auto preferred)',
    '3+ Magazines (minimum)',
    '150+ rounds of ammunition',
    'Eye protection (ANSI Z87.1 rated)',
    'Ear protection (electronic recommended)',
    'Outside waistband holster',
    'Magazine pouch/carrier',
    'Sturdy belt',
    'Tactical clothing',
    'Knee pads (optional)',
    'Water & snacks',
  ],
  'rifle-basics': [
    'Rifle (AR-15 platform or similar)',
    '3+ Magazines',
    '50+ rounds of ammunition',
    'Eye protection (ANSI Z87.1 rated)',
    'Ear protection (muffs recommended)',
    'Sling (1-point or 2-point)',
    'Sturdy belt',
    'Closed-toe boots/shoes',
    'Hat with brim',
    'Water bottle',
  ],
  'long-distance': [
    'Rifle with scope',
    '100+ rounds of match-grade ammo',
    'Spotting scope (if available)',
    'Shooting mat',
    'Eye & ear protection',
    'Bipod or front rest',
    'Wind meter (if available)',
    'Ballistic calculator (app OK)',
    'Sunscreen & hat',
    'Water & snacks',
    'Notebook for dope data',
  ],
  'shotgun-training': [
    'Pump or semi-auto shotgun',
    '25+ rounds of buckshot',
    '10+ rounds of birdshot',
    'Eye protection (ANSI Z87.1 rated)',
    'Ear protection (muffs recommended)',
    'Shell holder/carrier',
    'Sturdy belt',
    'Closed-toe shoes',
    'Water bottle',
  ],
  'safety-fundamentals': [
    'Handgun or rifle (rentals available)',
    'Eye protection',
    'Ear protection',
    'Comfortable clothing',
    'Closed-toe shoes',
    'Notebook & pen',
    'Water bottle',
  ],
  'gun-cleaning': [
    'Your firearm (field-stripped or assembled)',
    'Cleaning kit (if available)',
    'Cleaning solvent',
    'Lubricating oil',
    'Cleaning patches/brushes',
    'Old towel or mat',
    'Nitrile gloves',
  ],
  'tourniquet-course': [
    'CAT Tourniquet (we can provide)',
    'Gauze/pressure bandage',
    'Chest seal (demo)',
    'NPA (demo)',
    'Sharpie marker',
    'Notepad & pen',
    'Comfortable clothing',
  ],
  'live-fire-tactics': [
    'Handgun (semi-auto)',
    '3+ Magazines',
    '200+ rounds of ammunition',
    'Eye & ear protection',
    'OWB holster & mag pouches',
    'Tactical belt',
    'Tactical clothing',
    'Knee pads',
    'Water & snacks',
    'Flashlight',
  ],
  'uts-program': [
    'NO LIVE GUNS OR AMMO',
    'Long sleeves (removable)',
    'Long pants',
    'Tennis shoes or boots',
    'Neck protection',
    'Gloves',
    'Readiness to learn',
    'Positive attitude',
  ],
};

const SAFETY_RULES = [
  { rule: 'Treat every firearm as if it is loaded', detail: 'Always assume a firearm is loaded. Never take anyone\'s word that a gun is unloaded. Verify yourself.' },
  { rule: 'Never point a firearm at anything you are not willing to destroy', detail: 'Always maintain muzzle awareness. Keep the firearm pointed in a safe direction at all times.' },
  { rule: 'Keep your finger off the trigger until you are ready to fire', detail: 'Finger must remain along the frame/receiver until your sights are on target and you have made the decision to fire.' },
  { rule: 'Be sure of your target and what is beyond it', detail: 'Know what your target is, what is in front of it, and what is behind it. Bullets can pass through targets.' },
  { rule: 'All firearms must be cased and unloaded when entering/exiting the facility', detail: 'Firearms must be unloaded and in a case before entering the building. Only load on the firing line.' },
  { rule: 'No alcohol or drugs before or during training', detail: 'Any student suspected of being under the influence will be immediately removed from training. Zero tolerance.' },
  { rule: 'Follow all instructor commands immediately', detail: 'When the Range Safety Officer or instructor gives a command, comply immediately without question.' },
  { rule: 'Report any safety concerns immediately', detail: 'If you observe any unsafe behavior or equipment issues, report to the instructor immediately.' },
];

const GALLERY_IMAGES = [
  { url: gallery1, caption: 'Live Fire Training Session' },
  { url: gallery2, caption: 'Range Facility' },
  { url: gallery3, caption: 'Tactical Gear Setup' },
  { url: gallery4, caption: 'Precision Shooting Practice' },
  { url: gallery5, caption: 'Instructor Training' },
  { url: gallery6, caption: 'Student Group Training' },
  { url: gallery7, caption: 'Defensive Tactics' },
  { url: gallery8, caption: 'Range Day' },
  { url: gallery9, caption: 'Tactical Operations' },
  { url: gallery10, caption: 'Training Session' },
  { url: gallery11, caption: 'Tactical Training' },
  { url: gallery12, caption: 'Live Fire Exercise' },
];

const NAV_LINKS = [
  { id: 'about', label: 'About' },
  { id: 'services', label: 'Services' },
  { id: 'instructors', label: 'Instructors' },
  { id: 'uts', label: 'UTS Program' },
  { id: 'safety', label: 'Safety' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'contact', label: 'Contact' },
];

// ═══════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════

function StripeButtonSlot({ elementId }: { elementId: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const el = document.getElementById(elementId);
    if (el && !ref.current.contains(el)) {
      ref.current.appendChild(el);
    }
  }, [elementId]);
  return <div ref={ref} className="mt-4 pt-4 border-t border-white/10" />;
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-brand-black/95 backdrop-blur-md shadow-steel border-b border-white/5' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-3 group">
            <img src={logo} alt="3B Tactical" className="h-12 w-12 object-contain group-hover:scale-105 transition-transform" />
            <div className="hidden sm:block">
              <span className="font-heading text-xl font-bold text-white tracking-wider">3B TACTICAL</span>
              <span className="block text-[10px] text-brand-steel tracking-[0.3em] font-body">PREMIER FIREARMS TRAINING</span>
            </div>
          </button>

          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="px-3 py-2 text-sm font-body text-brand-silver hover:text-white hover:bg-white/5 rounded transition-all duration-200 tracking-wide uppercase"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo('calendar')}
              className="ml-3 px-5 py-2.5 bg-brand-red hover:bg-brand-red-dark text-white text-sm font-heading font-semibold tracking-wider uppercase rounded transition-all duration-200 hover:shadow-tactical"
            >
              Book Now
            </button>
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white p-2">
            <div className="w-6 h-5 relative flex flex-col justify-between">
              <span className={`block h-0.5 bg-white transition-all duration-300 origin-center ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
              <span className={`block h-0.5 bg-white transition-all duration-300 origin-center ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden transition-all duration-300 overflow-hidden ${mobileOpen ? 'max-h-[500px] bg-brand-black/98 border-t border-white/5' : 'max-h-0'}`}>
        <div className="px-4 py-4 space-y-1">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className="block w-full text-left px-4 py-3 text-brand-silver hover:text-white hover:bg-white/5 rounded transition-all font-body tracking-wide uppercase text-sm"
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => scrollTo('calendar')}
            className="block w-full mt-3 px-4 py-3 bg-brand-red text-white text-center font-heading font-semibold tracking-wider uppercase rounded"
          >
            Book Now
          </button>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        >
          <source src={videoBg} type="video/mp4" />
        </video>
        <div className="video-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center max-w-5xl mx-auto px-4">
        <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl font-bold text-white tracking-wider mb-4 animate-slide-up">
          3B <span className="text-brand-red">TACTICAL</span>
        </h1>
        
        <div className="w-24 h-1 bg-brand-red mx-auto mb-6 animate-slide-up" style={{ animationDelay: '0.2s' }} />
        
        <p className="text-brand-silver text-lg sm:text-xl font-body font-light tracking-wide max-w-2xl mx-auto mb-10 animate-slide-up" style={{ animationDelay: '0.4s' }}>
          Premier Firearms Training for Civilians & Professional Gun Carriers in Miami, FL
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.6s' }}>
          <button
            onClick={() => scrollTo('calendar')}
            className="px-8 py-4 bg-brand-red hover:bg-brand-red-dark text-white font-heading font-semibold text-lg tracking-wider uppercase rounded transition-all duration-300 hover:shadow-tactical-lg pulse-red"
          >
            Book a Session
          </button>
          <button
            onClick={() => scrollTo('about')}
            className="px-8 py-4 border-2 border-brand-silver/30 hover:border-brand-silver text-white font-heading font-semibold text-lg tracking-wider uppercase rounded transition-all duration-300 hover:bg-white/5"
          >
            Learn More
          </button>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-brand-silver/40 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-brand-red rounded-full" />
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ tag, title, subtitle }: { tag: string; title: string; subtitle?: string }) {
  return (
    <div className="text-center mb-16">
      <span className="text-brand-red font-heading text-sm tracking-[0.4em] uppercase font-semibold">{tag}</span>
      <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-3 tracking-wider">
        {title}
      </h2>
      <div className="w-20 h-1 bg-brand-red mx-auto mt-6 mb-4" />
      {subtitle && <p className="text-brand-steel text-lg max-w-2xl mx-auto font-body font-light">{subtitle}</p>}
    </div>
  );
}

function About() {
  return (
    <section id="about" className="py-24 bg-brand-black metal-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader tag="Who We Are" title="About 3B Tactical" />
        
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-brand-silver text-lg font-body leading-relaxed mb-6">
              Welcome to 3B Tactical, Miami's premier firearms training company, dedicated to developing responsible, confident, and highly skilled gun owners and professionals.
            </p>
            <p className="text-brand-steel text-base font-body leading-relaxed mb-6">
              Whether you are a civilian seeking to improve your personal safety or a security or law enforcement professional looking to sharpen your tactical abilities, our comprehensive training programs are designed to meet your goals. Every course emphasizes firearm safety, sound judgment, legal responsibility, and practical defensive skills that translate to real-world situations.
            </p>
            <p className="text-brand-steel text-base font-body leading-relaxed mb-6">
              Our experienced instructors bring decades of combined backgrounds in military service, private security, and firearms instruction, providing students with professional, hands-on training in a supportive learning environment.
            </p>
            <p className="text-brand-steel text-base font-body leading-relaxed mb-6">
              As a proud mentoring partner with iProtectUSA through the Unlimited Tactical Solutions (UTS) program, we offer advanced defensive firearms and simulation-based training in our state-of-the-art 25,000-square-foot training facility.
            </p>
            <p className="text-brand-steel text-base font-body leading-relaxed mb-8">
              At 3B Tactical, we don't just teach you how to shoot—we teach you how to think, make sound decisions under pressure, and carry with confidence and responsibility.
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              {[
                { num: '10+', label: 'Years Experience' },
                { num: '5000+', label: 'Students Trained' },
                { num: '25K', label: 'Sq Ft Facility' },
                { num: '24/7', label: 'Concierge Service' },
              ].map((stat, i) => (
                <div key={i} className="bg-brand-darker border border-white/5 p-4 rounded text-center">
                  <div className="text-brand-red font-heading text-3xl font-bold">{stat.num}</div>
                  <div className="text-brand-steel text-sm font-body mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-lg overflow-hidden shadow-2xl">
              <img
                src={aboutBg}
                alt="3B Tactical Training Session"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-black/60 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-brand-red px-6 py-3 rounded shadow-tactical">
              <span className="font-heading text-white font-bold tracking-wider uppercase text-sm">Miami, FL</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const services = [
    {
      icon: '🎯',
      title: 'PISTOL FUNDAMENTALS',
      description: 'Master the essentials of pistol handling, safety, and marksmanship. 2-hour comprehensive course for beginners.',
      features: ['Safety Fundamentals', 'Pistol Handling', 'Marksmanship', 'Range Time'],
      price: 150,
      duration: '2 hours',
    },
    {
      icon: '⭐',
      title: '2A Concierge Service',
      description: 'Our 2A Concierge service helps with all your Second Amendment and tactical needs. From personalized consultations to specialized equipment recommendations.',
      features: ['Equipment Consultations', 'Firearm Sourcing', 'Accessory Recommendations', 'Ammo Supply', 'Training Suggestions', 'Tactical Products'],
    },
    {
      icon: '⚔️',
      title: 'UTS Gun Defensive Training',
      description: 'Mentoring partner with iProtectUSA within the Unlimited Tactical Solutions program. Scenario-based training in our 25,000 sq ft warehouse with indoor town and range.',
      features: ['Scenario-Based Training', 'Indoor Town Layout', 'Gun Range Built-In', 'Weekly Sessions', 'All Skill Levels', 'Year-Round Program'],
    },
    {
      icon: '🛡️',
      title: '3B Tactical Products',
      description: 'Tactical products, accessories, apparel and gear for professional gun carriers and civilians. 3B Tactical t-shirts, apparel and other clothing available for purchase.',
      features: ['Tactical Accessories', 'Pistol Accessories', 'Rifle Accessories', '3B Apparel', 'Custom Gear', 'Tactical Products'],
    },
    {
      icon: '🔥',
      title: 'Live Fire Training',
      description: 'Intensive hands-on live fire session with expert instructors in our state-of-the-art facility.',
      features: ['Live Fire Drills', 'Expert Instructors', 'Tactical Scenarios', 'Safety Briefing', 'Hands-On Training', 'Limited Spots'],
      date: 'August 30, 2025',
    },
  ];

  const usLawShieldService = {
    icon: '⚖️',
    title: 'U.S. LawShield',
    description: 'Protect yourself with U.S. LawShield — comprehensive legal defense coverage for lawfully armed Americans. Know your rights and carry with confidence knowing you have legal protection when it matters most.',
    features: ['Legal Defense Coverage', '24/7 Emergency Hotline', 'Self-Defense Protection', 'Firearm Owner Rights', 'Nationwide Coverage', 'Peace of Mind'],
    logo: uslawshieldLogo,
    link: 'https://www.uslawshield.com/?affid=8cda5e6f-763e-11f1-b538-06ed5a46dc49',
  };

  return (
    <section id="services" className="relative py-24 overflow-hidden bg-brand-black">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader tag="What We Offer" title="Our Services" subtitle="Comprehensive firearms training and tactical services for every level of experience" />
        
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, i) => (
            <div key={i} className="bg-brand-black border border-white/5 rounded-lg p-8 hover:border-brand-red/30 transition-all duration-300 group hover:shadow-tactical">
              <div className="text-4xl mb-4">{service.icon}</div>
              <h3 className="font-heading text-2xl font-bold text-white tracking-wider mb-3 group-hover:text-brand-red transition-colors">{service.title}</h3>
              {service.price !== undefined && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-brand-red font-heading font-bold text-lg">${service.price}</span>
                  <span className="text-brand-steel font-body text-sm">/ {service.duration}</span>
                </div>
              )}
              <p className="text-brand-steel font-body leading-relaxed mb-6">{service.description}</p>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {service.features.map((f, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm text-brand-silver">
                    <div className="w-1.5 h-1.5 bg-brand-red rounded-full flex-shrink-0" />
                    <span className="font-body">{f}</span>
                  </div>
                ))}
              </div>
              {i === 0 && <StripeButtonSlot elementId="stripe-pistol" />}
              {i === 2 && <StripeButtonSlot elementId="stripe-uts" />}
              {i === 4 && <StripeButtonSlot elementId="stripe-livefire" />}
            </div>
          ))}

          {/* U.S. LawShield Partner Card */}
          <a
            href={usLawShieldService.link}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-brand-black border border-blue-900/30 rounded-lg p-8 hover:border-blue-500/50 transition-all duration-300 group hover:shadow-lg block"
          >
            <div className="flex items-center gap-4 mb-4">
              <img
                src={usLawShieldService.logo}
                alt="U.S. LawShield"
                className="h-16 w-auto object-contain"
              />
            </div>
            <h3 className="font-heading text-2xl font-bold text-white tracking-wider mb-3 group-hover:text-blue-400 transition-colors">{usLawShieldService.title}</h3>
            <p className="text-brand-steel font-body leading-relaxed mb-6">{usLawShieldService.description}</p>
            <div className="grid grid-cols-2 gap-2 mb-6">
              {usLawShieldService.features.map((f, j) => (
                <div key={j} className="flex items-center gap-2 text-sm text-brand-silver">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />
                  <span className="font-body">{f}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-2 py-3 bg-blue-900/30 hover:bg-blue-800/40 border border-blue-500/30 rounded-xl text-white font-heading font-bold text-sm tracking-wider uppercase transition-all duration-300">
              <span>Learn More</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}

function Instructors() {
  const [active, setActive] = useState(0);

  return (
    <section id="instructors" className="py-24 bg-brand-black metal-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader tag="Meet Our Team" title="Expert Instructors" subtitle="Our instructors bring decades of military, law enforcement, and tactical training experience" />
        
        {/* Instructor tabs */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          {INSTRUCTORS.map((inst, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`px-6 py-3 font-heading font-semibold tracking-wider uppercase text-sm rounded transition-all duration-300 ${
                active === i
                  ? 'bg-brand-red text-white shadow-tactical'
                  : 'bg-brand-grey text-brand-silver hover:bg-brand-darker hover:text-white border border-white/10'
              }`}
            >
              {inst.name}
            </button>
          ))}
        </div>

        {/* Active instructor */}
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-1">
            <div className="relative rounded-lg overflow-hidden group">
              <img
                src={INSTRUCTORS[active].image}
                alt={INSTRUCTORS[active].name}
                className="w-full h-auto max-h-[600px] object-contain group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-heading text-2xl font-bold text-white tracking-wider">{INSTRUCTORS[active].name}</h3>
                <p className="text-brand-red font-heading text-sm tracking-wider uppercase mt-1">{INSTRUCTORS[active].title}</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="bg-brand-darker border border-white/5 rounded-lg p-8">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <span className="text-brand-steel text-xs font-body tracking-wider uppercase">Military Background</span>
                  <p className="text-white font-body font-semibold mt-1">{INSTRUCTORS[active].military}</p>
                </div>
                <div>
                  <span className="text-brand-steel text-xs font-body tracking-wider uppercase">Experience</span>
                  <p className="text-white font-body font-semibold mt-1">{INSTRUCTORS[active].experience}</p>
                </div>
              </div>

              <div className="mb-6">
                <span className="text-brand-steel text-xs font-body tracking-wider uppercase block mb-3">Specialties</span>
                <div className="flex flex-wrap gap-2">
                  {INSTRUCTORS[active].specialties.map((s, i) => (
                    <span key={i} className="px-3 py-1.5 bg-brand-grey border border-white/10 rounded text-sm text-brand-silver font-body">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <span className="text-brand-steel text-xs font-body tracking-wider uppercase block mb-3">Biography</span>
                <p className="text-brand-silver font-body leading-relaxed">{INSTRUCTORS[active].bio}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function UTSProgram() {
  return (
    <section id="uts" className="py-24 relative overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader tag="Unlimited Tactical Solutions" title="UTS Program" subtitle="Advanced gun defensive training in partnership with iProtectUSA" />
        
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="bg-brand-darker border border-brand-red/20 rounded-xl p-8 md:p-12 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-brand-red rounded-lg flex items-center justify-center shadow-tactical">
                  <span className="text-2xl">⚔️</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-white tracking-wider">UTS WEEKLY TRAINING</h3>
                  <p className="text-brand-red font-body text-sm">iProtectUSA Partnership</p>
                </div>
              </div>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 bg-brand-black/50 rounded-lg p-4">
                  <div className="text-brand-red text-xl mt-0.5">📅</div>
                  <div>
                    <span className="text-brand-steel text-sm font-body block">Schedule</span>
                    <span className="text-white font-heading font-bold text-lg tracking-wider">EVERY TUESDAY</span>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-brand-black/50 rounded-lg p-4">
                  <div className="text-brand-red text-xl mt-0.5">🕖</div>
                  <div>
                    <span className="text-brand-steel text-sm font-body block">Time</span>
                    <span className="text-white font-heading font-bold text-lg tracking-wider">7:00 PM - 10:00 PM</span>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-brand-black/50 rounded-lg p-4">
                  <div className="text-brand-red text-xl mt-0.5">📍</div>
                  <div>
                    <span className="text-brand-steel text-sm font-body block">Location</span>
                    <span className="text-white font-heading font-bold text-lg tracking-wider">7810 NW 98TH ST</span>
                    <span className="text-brand-silver font-body block text-sm">Hialeah, FL 33016</span>
                  </div>
                </div>
              </div>

              <a
                href="https://wa.me/17867526622?text=Hi%203B%20Tactical!%20I%20want%20to%20join%20the%20UTS%20Program."
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-4 bg-brand-red hover:bg-brand-red-dark text-white text-center font-heading font-bold text-lg tracking-wider uppercase rounded-lg transition-all duration-300 hover:shadow-tactical-lg"
              >
                Join UTS Program via WhatsApp
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-3xl font-bold text-white tracking-wider mb-6">Defensive Training <span className="text-brand-red">That Works</span></h3>
            <p className="text-brand-silver font-body leading-relaxed mb-6">
              The UTS program is designed for armed civilians and professional gun carriers. Our training takes place within a 25,000 square-foot warehouse including a small town and gun range built indoors.
            </p>
            <p className="text-brand-steel font-body leading-relaxed mb-8">
              This tactical training layout allows us to provide scenario-based training and preparation for armed crisis situations. These sessions help our clients understand what to do in a defensive situation, and also what not to do.
            </p>

            <div className="space-y-4">
              {[
                'Indoor tactical town layout for realistic scenarios',
                '25,000 sq ft state-of-the-art facility',
                'Weekly year-round training program',
                'All skill levels welcome',
                'Armed crisis situation preparation',
                'Mentoring partnership with iProtectUSA',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-brand-red/20 border border-brand-red/40 rounded flex items-center justify-center flex-shrink-0">
                    <svg className="w-3.5 h-3.5 text-brand-red" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-brand-silver font-body">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// CALENDAR & BOOKING
// ═══════════════════════════════════════════════════════════

function Calendar() {
  const [selectedCourse, setSelectedCourse] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState<any>(null);

  const today = new Date();
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // UTS only has 7:00 PM, other courses have standard times
  const availableTimes = selectedCourse === 'uts-program' 
    ? ['07:00 PM'] 
    : ['09:00 AM', '10:30 AM', '01:00 PM', '02:30 PM', '04:00 PM'];

  // UTS Tuesdays
  const isUTSDay = (day: number) => {
    const d = new Date(year, month, day);
    return d.getDay() === 2 && d >= today;
  };

  // Special events (August 30th Live Fire Training)
  const isSpecialEvent = (day: number) => {
    return month === 7 && day === 30; // August is 7 (0-indexed)
  };

  const isAvailable = (day: number) => {
    const d = new Date(year, month, day);
    // UTS is only available on Tuesdays
    if (selectedCourse === 'uts-program') {
      return isUTSDay(day);
    }
    // Other courses: not Sunday, and not UTS Tuesday (reserved for UTS)
    return d >= today && d.getDay() !== 0 && d.getDay() !== 2; 
  };

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1));

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const handleBook = (formData: any) => {
    const store = useBookingStore.getState();
    const booking = store.addBooking({
      courseType: selectedCourse || 'UTS Program',
      date: `${monthNames[month]} ${selectedDate}, ${year}`,
      time: selectedTime,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      notes: formData.notes,
    });
    setConfirmationData(booking);
    setShowBookingModal(false);
    setShowConfirmation(true);
  };

  return (
    <section id="calendar" className="py-24 bg-brand-darker">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader tag="Schedule Training" title="Book a Session" subtitle="Select your course, pick a date, and reserve your spot on the range" />

        {/* Course selector */}
        <div className="mb-8">
          <label className="block text-brand-steel text-sm font-body tracking-wider uppercase mb-3">Select Course</label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {COURSES.map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  setSelectedCourse(c.id);
                  setSelectedDate(null);
                  setSelectedTime('');
                }}
                className={`p-3 rounded-lg border text-left transition-all duration-200 ${
                  selectedCourse === c.id
                    ? 'border-brand-red bg-brand-red/10 shadow-tactical'
                    : 'border-white/10 bg-brand-black hover:border-brand-red/30'
                }`}
              >
                <span className="text-lg">{c.icon}</span>
                <p className="text-white font-body text-sm font-semibold mt-1">{c.name}</p>
                <p className="text-brand-steel text-xs font-body mt-0.5">{c.level}</p>
                {c.contactUs ? (
                  <p className="text-brand-red font-heading font-bold mt-1">Contact Us</p>
                ) : c.price > 0 ? (
                  <p className="text-brand-red font-heading font-bold mt-1">${c.price}</p>
                ) : c.price === null ? (
                  <p className="text-brand-steel font-heading font-bold mt-1">TBA</p>
                ) : (
                  <p className="text-green-400 font-heading font-bold mt-1">FREE</p>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-brand-black border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <button onClick={prevMonth} className="p-2 hover:bg-white/5 rounded text-brand-silver hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <h3 className="font-heading text-xl font-bold text-white tracking-wider">{monthNames[month]} {year}</h3>
              <button onClick={nextMonth} className="p-2 hover:bg-white/5 rounded text-brand-silver hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} className="text-center text-brand-steel text-xs font-heading tracking-wider py-2">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const available = isAvailable(day);
                const utsDay = isUTSDay(day);
                const specialEvent = isSpecialEvent(day);
                const selected = selectedDate === day;
                return (
                  <button
                    key={day}
                    onClick={() => available && setSelectedDate(day)}
                    disabled={!available}
                    className={`calendar-cell aspect-square rounded-lg flex flex-col items-center justify-center text-sm font-body border transition-all ${
                      selected
                        ? 'border-brand-red bg-brand-red text-white shadow-tactical'
                        : available
                          ? 'border-white/10 text-brand-silver hover:border-brand-red/50 bg-brand-darker'
                          : 'border-transparent text-brand-grey cursor-not-allowed'
                    }`}
                  >
                    <span className={selected ? 'font-bold' : ''}>{day}</span>
                    {specialEvent && (
                      <span className="text-[8px] text-brand-red font-bold tracking-wider mt-0.5">LIVE</span>
                    )}
                    {utsDay && !specialEvent && (
                      <span className="text-[8px] text-brand-red font-bold tracking-wider mt-0.5">UTS</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex flex-wrap gap-4 text-xs text-brand-steel font-body">
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-brand-red" /> Selected</div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-brand-darker border border-white/10" /> Available</div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-brand-darker border border-brand-red/50" /> UTS Tuesday</div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-brand-darker border border-brand-red/70" /> Live Fire</div>
            </div>
          </div>

          {/* Time slots & booking */}
          <div className="space-y-6">
            <div className="bg-brand-black border border-white/10 rounded-xl p-6">
              <h4 className="font-heading text-lg font-bold text-white tracking-wider mb-4">Select Time</h4>
              {selectedDate ? (
                <div className="space-y-2">
                  {availableTimes.map((t) => (
                    <button
                      key={t}
                      onClick={() => setSelectedTime(t)}
                      className={`block w-full py-3 px-4 rounded-lg text-left font-body text-sm transition-all border ${
                        selectedTime === t
                          ? 'border-brand-red bg-brand-red/10 text-white'
                          : 'border-white/10 text-brand-silver hover:border-brand-red/30 hover:bg-white/5'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-brand-steel text-sm font-body">Select a date to see available times</p>
              )}
            </div>

            {selectedCourse && selectedDate && selectedTime && (
              <button
                onClick={() => setShowBookingModal(true)}
                className="w-full py-4 bg-brand-red hover:bg-brand-red-dark text-white font-heading font-bold text-lg tracking-wider uppercase rounded-xl transition-all duration-300 hover:shadow-tactical-lg pulse-red"
              >
                Book This Session
              </button>
            )}

            {/* Quick WhatsApp */}
            <a
              href="https://wa.me/17867526622?text=Hi!%20I'd%20like%20to%20book%20a%20training%20session%20at%203B%20Tactical."
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center py-3 bg-green-600 hover:bg-green-700 text-white font-body font-semibold rounded-lg transition-all duration-200"
            >
              💬 Book via WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModal
          course={COURSES.find(c => c.id === selectedCourse)}
          date={`${monthNames[month]} ${selectedDate}, ${year}`}
          time={selectedTime}
          onClose={() => setShowBookingModal(false)}
          onBook={handleBook}
        />
      )}

      {/* Confirmation Modal */}
      {showConfirmation && confirmationData && (
        <ConfirmationModal booking={confirmationData} onClose={() => setShowConfirmation(false)} />
      )}
    </section>
  );
}

function BookingModal({ course, date, time, onClose, onBook }: any) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', notes: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.phone.trim()) e.phone = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onBook(form);
  };

  return (
    <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-brand-darker border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-xl font-bold text-white tracking-wider">Complete Your Booking</h3>
            <button onClick={onClose} className="text-brand-steel hover:text-white text-2xl">&times;</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-sm font-body">
            <span className="text-brand-red font-semibold">{course?.name}</span>
            <span className="text-brand-steel">•</span>
            <span className="text-brand-silver">{date}</span>
            <span className="text-brand-steel">•</span>
            <span className="text-brand-silver">{time}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">First Name *</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm"
                placeholder="John"
              />
              {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">Last Name *</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm"
                placeholder="Doe"
              />
              {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
            </div>
          </div>

          <div>
            <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm"
              placeholder="john@example.com"
            />
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">Phone *</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm"
              placeholder="(786) 752-6622"
            />
            {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">Additional Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm h-20 resize-none"
              placeholder="Any special requirements or questions..."
            />
          </div>

          <button
            type="submit"
            className="w-full py-4 bg-brand-red hover:bg-brand-red-dark text-white font-heading font-bold text-lg tracking-wider uppercase rounded-lg transition-all duration-300 hover:shadow-tactical-lg"
          >
            Confirm Booking — {course?.price > 0 ? `$${course.price}` : 'FREE'}
          </button>

          <p className="text-center text-brand-steel text-xs font-body">
            A confirmation will be sent to your email. For immediate assistance,{' '}
            <a href="https://wa.me/17867526622" target="_blank" rel="noopener noreferrer" className="text-brand-red hover:underline">message us on WhatsApp</a>
          </p>
        </form>
      </div>
    </div>
  );
}

function ConfirmationModal({ booking, onClose }: { booking: any; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-brand-darker border border-brand-red/30 rounded-2xl w-full max-w-md p-8 text-center shadow-tactical-lg" onClick={(e) => e.stopPropagation()}>
        <div className="w-20 h-20 bg-brand-red/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-brand-red" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-heading text-2xl font-bold text-white tracking-wider mb-2">Booking Confirmed!</h3>
        <p className="text-brand-steel font-body mb-6">Your training session has been reserved. A confirmation email will be sent to <span className="text-white">{booking.email}</span>.</p>
        
        <div className="bg-brand-black/50 rounded-lg p-4 text-left space-y-2 mb-6">
          <div className="flex justify-between">
            <span className="text-brand-steel text-sm font-body">Booking ID</span>
            <span className="text-white text-sm font-mono">{booking.id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-brand-steel text-sm font-body">Course</span>
            <span className="text-white text-sm font-body">{booking.courseType}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-brand-steel text-sm font-body">Date</span>
            <span className="text-white text-sm font-body">{booking.date}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-brand-steel text-sm font-body">Time</span>
            <span className="text-white text-sm font-body">{booking.time}</span>
          </div>
        </div>

        <a
          href={`https://wa.me/17867526622?text=${encodeURIComponent(`Booking Confirmed! ID: ${booking.id}\nCourse: ${booking.courseType}\nDate: ${booking.date} at ${booking.time}`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full py-3 bg-green-600 hover:bg-green-700 text-white font-body font-semibold rounded-lg mb-3 transition-all"
        >
          💬 Confirm via WhatsApp
        </a>

        <button
          onClick={onClose}
          className="w-full py-3 border border-white/20 text-brand-silver hover:text-white font-body rounded-lg transition-all"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// GEAR CHECKLIST
// ═══════════════════════════════════════════════════════════

function GearChecklist() {
  const [selectedCourse, setSelectedCourse] = useState('uts-program');
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});

  const items = GEAR_CHECKLIST[selectedCourse] || [];

  const toggleItem = (item: string) => {
    setCheckedItems(prev => ({ ...prev, [item]: !prev[item] }));
  };

  const checkedCount = items.filter(i => checkedItems[i]).length;

  return (
    <section id="gear" className="py-24 bg-brand-black metal-texture">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader tag="Be Prepared" title="Gear Checklist" subtitle="Make sure you have everything you need before arriving for your session" />

        {/* Course selector */}
        <div className="mb-8">
          <label className="block text-brand-steel text-sm font-body tracking-wider uppercase mb-3">Select your course to see required gear</label>
          <select
            value={selectedCourse}
            onChange={(e) => { setSelectedCourse(e.target.value); setCheckedItems({}); }}
            className="w-full bg-brand-darker border border-white/10 rounded-lg px-4 py-3 text-white font-body"
          >
            {COURSES.map(c => (
              <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
            ))}
          </select>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm font-body mb-2">
            <span className="text-brand-steel">Items packed</span>
            <span className="text-brand-red font-semibold">{checkedCount}/{items.length}</span>
          </div>
          <div className="w-full h-2 bg-brand-grey rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-red rounded-full transition-all duration-300"
              style={{ width: `${items.length > 0 ? (checkedCount / items.length) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Checklist */}
        <div className="bg-brand-darker border border-white/10 rounded-xl overflow-hidden">
          {items.map((item, i) => (
            <label
              key={i}
              className={`flex items-center gap-4 px-6 py-4 cursor-pointer transition-all border-b border-white/5 last:border-0 hover:bg-white/5 ${checkedItems[item] ? 'bg-brand-red/5' : ''}`}
            >
              <input
                type="checkbox"
                checked={!!checkedItems[item]}
                onChange={() => toggleItem(item)}
                className="tactical-checkbox"
              />
              <span className={`font-body text-sm ${checkedItems[item] ? 'text-brand-steel line-through' : 'text-white'}`}>
                {item}
              </span>
            </label>
          ))}
        </div>

        {checkedCount === items.length && items.length > 0 && (
          <div className="mt-6 bg-brand-red/10 border border-brand-red/30 rounded-lg p-4 text-center">
            <span className="text-brand-red font-heading font-bold tracking-wider uppercase">
              ✅ You're all packed and ready!
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// SAFETY PROTOCOLS
// ═══════════════════════════════════════════════════════════

function SafetyProtocols() {
  return (
    <section id="safety" className="py-24 bg-brand-darker">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader tag="Range Rules" title="Safety Protocols" subtitle="Your safety is our top priority. All participants must follow these rules at all times." />

        <div className="space-y-4">
          {SAFETY_RULES.map((item, i) => (
            <div key={i} className="bg-brand-black border border-white/10 rounded-xl p-6 hover:border-brand-red/20 transition-all duration-200 group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-brand-red/20 border border-brand-red/40 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-brand-red/30 transition-colors">
                  <span className="text-brand-red font-heading font-bold text-sm">{i + 1}</span>
                </div>
                <div>
                  <h4 className="font-heading text-lg font-bold text-white tracking-wider group-hover:text-brand-red transition-colors">{item.rule}</h4>
                  <p className="text-brand-steel font-body text-sm mt-2 leading-relaxed">{item.detail}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-brand-red/10 border border-brand-red/30 rounded-xl p-8 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h3 className="font-heading text-2xl font-bold text-white tracking-wider mb-3">Zero Tolerance Policy</h3>
          <p className="text-brand-silver font-body max-w-2xl mx-auto leading-relaxed">
            Any violation of safety protocols will result in immediate removal from the training session without refund. 
            Safety is non-negotiable at 3B Tactical. We reserve the right to refuse service to anyone who compromises the safety of our students and staff.
          </p>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// GALLERY
// ═══════════════════════════════════════════════════════════

function Gallery() {
  const [lightbox, setLightbox] = useState<number | null>(null);

  return (
    <section id="gallery" className="py-24 bg-brand-black metal-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader tag="See Us In Action" title="Training Gallery" subtitle="Take a look at our facilities, training sessions, and student experiences" />

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {GALLERY_IMAGES.map((img, i) => (
            <button
              key={i}
              onClick={() => setLightbox(i)}
              className="gallery-item rounded-xl overflow-hidden aspect-[4/3] relative group cursor-pointer border border-white/5 hover:border-brand-red/30 transition-all"
            >
              <img src={img.url} alt={img.caption} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute bottom-0 left-0 right-0 p-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-white font-heading text-sm tracking-wider font-semibold">{img.caption}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)} className="absolute -top-12 right-0 text-white text-3xl hover:text-brand-red transition-colors">&times;</button>
            <img src={GALLERY_IMAGES[lightbox].url} alt={GALLERY_IMAGES[lightbox].caption} className="w-full rounded-xl" />
            <p className="text-center text-brand-silver font-body mt-4">{GALLERY_IMAGES[lightbox].caption}</p>
            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={() => setLightbox((lightbox - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length)}
                className="px-4 py-2 bg-brand-grey hover:bg-brand-darker text-white rounded-lg font-body text-sm"
              >
                ← Previous
              </button>
              <button
                onClick={() => setLightbox((lightbox + 1) % GALLERY_IMAGES.length)}
                className="px-4 py-2 bg-brand-grey hover:bg-brand-darker text-white rounded-lg font-body text-sm"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// TESTIMONIALS
// ═══════════════════════════════════════════════════════════

function Testimonials() {
  const testimonials = [
    {
      text: "Damien Yates empowered me with extensive knowledge which I will be eternally grateful. His keen eye for detail, no nonsense demeanour when it comes to training and his extremely vast experience as it relates to gun fighting is next to none.",
      author: "Donovan C.E Smith",
      location: "Barbados",
    },
    {
      text: "3B Tactical gave me the confidence and skills I needed to become a responsible gun carrier. The instructors are top-notch and the training scenarios are realistic and challenging. Highly recommend their UTS program!",
      author: "Michael Torres",
      location: "Miami, FL",
    },
    {
      text: "I came in as a complete beginner and left feeling competent and confident. The safety-first approach combined with hands-on training makes 3B Tactical the best choice for firearms training in South Florida.",
      author: "Sarah Chen",
      location: "Hialeah, FL",
    },
  ];

  return (
    <section className="py-24 bg-brand-darker">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader tag="Testimonials" title="What People Say" />
        
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-brand-black border border-white/10 rounded-xl p-8 hover:border-brand-red/20 transition-all duration-300 group">
              <div className="text-brand-red text-4xl font-heading mb-4">"</div>
              <p className="text-brand-silver font-body leading-relaxed mb-6 text-sm">{t.text}</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-grey rounded-full flex items-center justify-center">
                  <span className="text-white font-heading font-bold text-sm">{t.author[0]}</span>
                </div>
                <div>
                  <p className="text-white font-body font-semibold text-sm">{t.author}</p>
                  <p className="text-brand-steel text-xs font-body">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// CONTACT & FOOTER
// ═══════════════════════════════════════════════════════════

function Contact() {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = encodeURIComponent(
      `Contact Form Submission\nName: ${form.firstName} ${form.lastName}\nEmail: ${form.email}\nPhone: ${form.phone}\nSubject: ${form.subject}\nMessage: ${form.message}`
    );
    window.open(`https://wa.me/17867526622?text=${msg}`, '_blank');
    setSent(true);
    setTimeout(() => setSent(false), 5000);
    setForm({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <section id="contact" className="py-24 bg-brand-black metal-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader tag="Get In Touch" title="Contact Us" subtitle="Ready to start training? Have questions? We're here to help." />

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="font-heading text-2xl font-bold text-white tracking-wider mb-6">Reach Out to Us</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-red/20 border border-brand-red/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">📍</span>
                  </div>
                  <div>
                    <span className="text-brand-steel text-xs font-body tracking-wider uppercase block mb-1">Location</span>
                    <span className="text-white font-body font-semibold">7810 NW 98th St, Hialeah, FL 33016</span>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-red/20 border border-brand-red/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">📱</span>
                  </div>
                  <div>
                    <span className="text-brand-steel text-xs font-body tracking-wider uppercase block mb-1">WhatsApp / Phone</span>
                    <a href="https://wa.me/17867526622" target="_blank" rel="noopener noreferrer" className="text-white font-body font-semibold hover:text-brand-red transition-colors">
                      1 (786) 752-6622
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-red/20 border border-brand-red/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">✉️</span>
                  </div>
                  <div>
                    <span className="text-brand-steel text-xs font-body tracking-wider uppercase block mb-1">Email</span>
                    <a href="mailto:3btactical@gmail.com" className="text-white font-body font-semibold hover:text-brand-red transition-colors">
                      3btactical@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-red/20 border border-brand-red/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">⏰</span>
                  </div>
                  <div>
                    <span className="text-brand-steel text-xs font-body tracking-wider uppercase block mb-1">UTS Program Hours</span>
                    <span className="text-white font-body font-semibold">Every Tuesday: 7:00 PM - 10:00 PM</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick action buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://wa.me/17867526622?text=Hi!%20I'd%20like%20to%20learn%20more%20about%203B%20Tactical."
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-4 bg-green-600 hover:bg-green-700 text-white text-center font-heading font-bold tracking-wider uppercase rounded-lg transition-all duration-200"
              >
                💬 WhatsApp Us
              </a>
              <a
                href="tel:+17867526622"
                className="flex-1 py-4 bg-brand-red hover:bg-brand-red-dark text-white text-center font-heading font-bold tracking-wider uppercase rounded-lg transition-all duration-200"
              >
                📞 Call Now
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="bg-brand-darker border border-white/10 rounded-xl p-8">
            <h3 className="font-heading text-xl font-bold text-white tracking-wider mb-6">Send a Message</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">First Name</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">Last Name</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">Subject</label>
                <select
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm"
                  required
                >
                  <option value="">Select a topic...</option>
                  <option value="Firearms Training">Firearms Training</option>
                  <option value="UTS Program">UTS Program</option>
                  <option value="Concierge Services">Concierge Services</option>
                  <option value="Schedule Appointment">Schedule Appointment</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              <div>
                <label className="block text-brand-steel text-xs font-body tracking-wider uppercase mb-1.5">Message</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-brand-black border border-white/10 rounded-lg px-4 py-3 text-white font-body text-sm h-32 resize-none"
                  placeholder="Tell us how we can help you..."
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-brand-red hover:bg-brand-red-dark text-white font-heading font-bold text-lg tracking-wider uppercase rounded-lg transition-all duration-300 hover:shadow-tactical-lg"
              >
                {sent ? '✓ Message Sent!' : 'Send Message'}
              </button>
              <p className="text-brand-steel text-xs font-body text-center">
                Your message will be sent directly to our WhatsApp for the fastest response.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-brand-black border-t border-white/5 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={logo} alt="3B Tactical" className="h-10 w-10 object-contain" />
              <span className="font-heading text-lg font-bold text-white tracking-wider">3B TACTICAL</span>
            </div>
            <p className="text-brand-steel text-sm font-body leading-relaxed">
              Premier firearms training for civilians and professional gun carriers in Miami, FL. Safety, responsibility, and skill development.
            </p>
          </div>

          <div>
            <h4 className="font-heading text-sm font-bold text-white tracking-wider uppercase mb-4">Quick Links</h4>
            <div className="space-y-2">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => document.getElementById(link.id)?.scrollIntoView({ behavior: 'smooth' })}
                  className="block text-brand-steel text-sm font-body hover:text-brand-red transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading text-sm font-bold text-white tracking-wider uppercase mb-4">Contact Info</h4>
            <div className="space-y-3 text-sm font-body">
              <p className="text-brand-steel">📍 7810 NW 98th St, Hialeah, FL 33016</p>
              <a href="https://wa.me/17867526622" className="block text-brand-steel hover:text-brand-red transition-colors">📱 1 (786) 752-6622</a>
              <a href="mailto:3btactical@gmail.com" className="block text-brand-steel hover:text-brand-red transition-colors">✉️ 3btactical@gmail.com</a>
            </div>
            <div className="mt-6">
              <h4 className="font-heading text-sm font-bold text-white tracking-wider uppercase mb-3">Legal Protection</h4>
              <a 
                href="https://www.uslawshield.com/?affid=8cda5e6f-763e-11f1-b538-06ed5a46dc49" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-red/20 border border-brand-red/40 rounded-lg text-brand-red text-sm font-body hover:bg-brand-red/30 transition-colors"
              >
                🛡️ US Law Shield - Get Protected
              </a>
            </div>
            <div className="mt-6">
              <h4 className="font-heading text-sm font-bold text-white tracking-wider uppercase mb-3">Follow Us</h4>
              <div className="flex flex-wrap gap-3">
                <a href="https://www.instagram.com/3btactical?igsh=MmR2bjF3dDQ5Z3Jz" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-pink-600 border border-white/20 rounded-lg flex items-center justify-center transition-all duration-200" aria-label="Instagram">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://www.threads.com/@3btactical" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-black border border-white/20 rounded-lg flex items-center justify-center transition-all duration-200" aria-label="Threads">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.086.718 5.496 2.057 7.164 1.432 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.34-.775-.963-1.393-1.803-1.79-.128 2.754-1.19 5.072-3.846 5.072-.036 0-.073 0-.11-.002-2.646-.155-4.384-1.649-4.52-3.71-.107-1.558.525-2.928 1.696-3.738.995-.686 2.265-1.001 3.495-.908.197.015.394.038.589.069-.282-.872-.734-1.553-1.352-2.033-.657-.514-1.438-.795-2.334-.795h-.012c-1.067.012-2.05.323-2.923.886V5.17c1.05-.538 2.313-.837 3.723-.837h.017c1.396 0 2.732.345 3.853.993 1.026.591 1.867 1.44 2.484 2.504.674 1.162 1.061 2.534 1.14 4.066.039.76.013 1.527-.075 2.286.285.93.417 1.956.173 3.206-.49 2.52-2.302 3.657-4.595 3.743h-.056c-1.43-.053-2.673-.423-3.696-1.078-1.156-.74-1.987-1.757-2.46-2.998-.194-.513-.33-1.06-.4-1.627-.049-.406-.078-.818-.078-1.236 0-.435.032-.864.092-1.284.217-1.51.85-2.832 1.836-3.825.872-.881 1.973-1.49 3.226-1.774-.044-.195-.077-.396-.097-.603-.2-2.095-.891-3.782-2.077-4.968C14.961.374 13.033-.084 10.797.004h-.003C8.558.084 6.626.546 5.113 1.46c-1.185.72-2.192 1.683-2.977 2.862l1.726 1.153c.575-1.002 1.36-1.815 2.313-2.395.853-.522 1.857-.856 2.954-.946h.02c1.732-.04 3.153.466 4.175 1.455.826.798 1.373 1.88 1.645 3.266.196.994.219 2.055.065 3.124.869.056 1.667.274 2.358.639.725.382 1.298.92 1.694 1.597.52.892.677 2.137.46 3.545z"/></svg>
                </a>
                <a href="https://www.facebook.com/share/1DDENUonuU" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-blue-600 border border-white/20 rounded-lg flex items-center justify-center transition-all duration-200" aria-label="Facebook">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://x.com/3b_tactical" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-black border border-white/20 rounded-lg flex items-center justify-center transition-all duration-200" aria-label="X (Twitter)">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://g.page/r/CcNIgxrbjIpYEBM/review" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-yellow-500 border border-white/20 rounded-lg flex items-center justify-center transition-all duration-200" aria-label="Google Review">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="tactical-divider mb-8" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-brand-grey text-xs font-body">
            &copy; {new Date().getFullYear()} 3B Tactical. All Rights Reserved.
          </p>
          <p className="text-brand-grey text-xs font-body">
            7810 NW 98th St • Hialeah, FL 33016 • Miami, FL
          </p>
        </div>
      </div>
    </footer>
  );
}


// ═══════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════

function LiveFirePopup({ onClose }: { onClose: () => void }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-300"
      style={{ backgroundColor: 'rgba(0,0,0,0.85)', opacity: visible ? 1 : 0 }}
      onClick={handleClose}
    >
      <div
        className="relative max-w-md w-full rounded-2xl overflow-hidden shadow-tactical-lg animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white text-xl hover:bg-brand-red hover:border-brand-red transition-all duration-200"
          aria-label="Close popup"
        >
          ×
        </button>

        {/* Flyer image */}
        <img
          src={liveFirePopup}
          alt="3B Tactical Live Fire Tactics Training — October 25th at 1:00 PM, Homestead Training Center"
          className="w-full h-auto"
        />

        {/* CTA Button */}
        <div className="bg-brand-black px-4 py-4 flex flex-col sm:flex-row gap-3">
          <a
            href="https://buy.stripe.com/4gMfZh8pjh291Np1oegUM0d?locale=en"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-3 bg-brand-red hover:bg-brand-red-dark text-white font-heading font-bold text-sm tracking-wider uppercase rounded-xl text-center transition-all duration-300 hover:shadow-tactical"
          >
            Register Now
          </a>
          <button
            onClick={handleClose}
            className="py-3 px-6 bg-brand-grey/50 hover:bg-brand-grey text-brand-steel hover:text-white font-heading text-sm tracking-wider uppercase rounded-xl transition-all duration-300"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showLiveFirePopup, setShowLiveFirePopup] = useState(() => {
    return !sessionStorage.getItem('liveFirePopupDismissed');
  });

  const handlePopupClose = () => {
    sessionStorage.setItem('liveFirePopupDismissed', '1');
    setShowLiveFirePopup(false);
  };

  return (
    <div className="min-h-screen bg-brand-black text-white">
      <Navbar />
      <Hero />
      <About />
      <Services />
      <Instructors />
      <UTSProgram />
      <Calendar />
      <GearChecklist />
      <SafetyProtocols />
      <Gallery />
      <Testimonials />
      <Contact />
      <Footer />

      {/* Live Fire Training Popup */}
      {showLiveFirePopup && <LiveFirePopup onClose={handlePopupClose} />}
    </div>
  );
}
